<script setup lang="ts">
import type { VirtualGift } from '@/types/api';
import { GIFT_META } from '@/types/api';

defineProps<{
  gifts: VirtualGift[];
}>();
</script>

<template>
  <section v-if="gifts.length > 0" class="gifts">
    <h2>Подарки</h2>
    <div class="gifts__grid">
      <div
        v-for="gift in gifts"
        :key="gift.id"
        class="gifts__item"
        :class="{ 'gifts__item--hoverable': gift.showMessageOnHover && gift.message }"
      >
        <span class="gifts__emoji">{{ GIFT_META[gift.giftType]?.emoji ?? '🎁' }}</span>

        <div v-if="gift.showMessageOnHover && gift.message" class="gifts__tooltip">
          <p>{{ gift.message }}</p>
          <small v-if="gift.nickname">от {{ gift.nickname }}</small>
          <small v-else-if="gift.isAnonymous">анонимно</small>
        </div>

        <div v-else-if="gift.message" class="gifts__visible-msg">
          <p>{{ gift.message }}</p>
          <small v-if="gift.nickname">от {{ gift.nickname }}</small>
          <small v-else-if="gift.isAnonymous">анонимно</small>
        </div>
      </div>
    </div>
  </section>

  <p v-else class="gifts__empty">Подарков пока нет — будьте первым!</p>
</template>

<style scoped>
.gifts h2 {
  margin: 0 0 0.75rem;
  color: #831843;
}

.gifts__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.gifts__item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  min-width: 4.5rem;
  padding: 0.75rem;
  border-radius: 1rem;
  background: linear-gradient(135deg, #fff1f2, #fdf2f8);
  border: 2px solid #fbcfe8;
}

.gifts__emoji {
  font-size: 2.2rem;
  line-height: 1;
}

.gifts__item--hoverable:hover .gifts__tooltip {
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, 0);
}

.gifts__tooltip {
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 50%;
  transform: translate(-50%, 4px);
  min-width: 180px;
  max-width: 260px;
  padding: 0.65rem 0.85rem;
  border-radius: 0.75rem;
  background: #831843;
  color: #fff;
  box-shadow: 0 8px 24px rgb(131 24 67 / 25%);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;
  z-index: 10;
  pointer-events: none;
}

.gifts__tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #831843;
}

.gifts__tooltip p {
  margin: 0 0 0.35rem;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.9rem;
  line-height: 1.4;
}

.gifts__tooltip small {
  opacity: 0.85;
  font-size: 0.75rem;
}

.gifts__visible-msg {
  text-align: center;
  max-width: 140px;
}

.gifts__visible-msg p {
  margin: 0;
  font-size: 0.78rem;
  color: #9d174d;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
}

.gifts__visible-msg small {
  color: #be185d;
  font-size: 0.72rem;
}

.gifts__empty {
  margin: 0;
  color: #9d174d;
}
</style>
